<template>
  <div class="login-page">
    <div class="login-panel">
      <input
        v-model.trim="form.mobile"
        class="login-input full"
        type="tel"
        maxlength="11"
        placeholder="请输入手机号"
      />

      <div class="code-row">
        <input
          v-model.trim="form.smsCode"
          class="login-input"
          type="text"
          maxlength="6"
          placeholder="请输入验证码"
        />
        <button class="captcha-btn" :disabled="isCaptchaLoading || countdown > 0" @click="handleGetSmsCode">
          {{ countdown > 0 ? `${countdown}s后重试` : '获取验证码' }}
        </button>
      </div>

      <label class="agreement">
        <input v-model="agreePolicy" type="checkbox" />
        <span>我已阅读并同意 <em>服务协议</em> 和 <em>隐私协议</em></span>
      </label>

      <button class="login-btn" :disabled="isLoginLoading" @click="handleLogin">登录</button>

      <img class="login-bottom-image" src="@/assets/img/login/login-img.png" alt="党建宣传图" />
    </div>

    <el-dialog
      v-model="captchaDialogVisible"
      title="图形验证码"
      width="66%"
      append-to-body
      destroy-on-close
    >
      <div class="captcha-dialog-content">
        <img
          v-if="captchaForm.captchaImg"
          class="captcha-image"
          :src="captchaForm.captchaImg"
          alt="图形验证码"
          @click="refreshGraphCaptcha"
        />
        <el-input
          class="captcha-input"
          v-model.trim="captchaForm.captcha"
          placeholder="请输入图形验证码"
          maxlength="8"
          clearable
        />
      </div>
      <template #footer>
        <div class="captcha-footer-actions">
          <el-button @click="captchaDialogVisible = false">取消</el-button>
          <el-button type="primary" :loading="isSendingSms" @click="confirmSendSms">确定</el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { onBeforeUnmount, onMounted, reactive, ref } from 'vue'
import { useRouter } from 'vue-router'
import { getLoginCaptcha, getMobileCaptcha, mobileLogin } from '@/api'
import { getToken, setToken } from '@/utils/auth'
import toast from '@/components/toast'

const MOBILE_PATTERN = /^1\d{10}$/
const SMS_CODE_PATTERN = /^\d{4,6}$/
const router = useRouter()

const form = reactive({
  mobile: '',
  smsCode: '',
})

const captchaForm = reactive({
  captcha: '',
  captchaKey: '',
  captchaImg: '',
})

const agreePolicy = ref(false)
const isCaptchaLoading = ref(false)
const isSendingSms = ref(false)
const isLoginLoading = ref(false)
const captchaDialogVisible = ref(false)
const countdown = ref(0)
let countdownTimer = null

onMounted(() => {
  if (getToken()) {
    router.replace('/home')
  }
})

function validateMobile() {
  if (!MOBILE_PATTERN.test(form.mobile)) {
    toast('请输入正确的11位手机号')
    return false
  }
  return true
}

async function refreshGraphCaptcha() {
  if (!validateMobile()) {
    return
  }
  isCaptchaLoading.value = true
  try {
    const res = await getLoginCaptcha({ mobile: form.mobile })
    const captchaData = res?.data?.captcha
    if (!captchaData?.key || !captchaData?.img) {
      toast('获取图形验证码失败，请稍后重试')
      return
    }
    captchaForm.captchaKey = captchaData.key
    captchaForm.captchaImg = captchaData.img
    captchaForm.captcha = ''
    captchaDialogVisible.value = true
  } catch (error) {
    // 错误提示已在全局请求拦截器中统一处理
  } finally {
    isCaptchaLoading.value = false
  }
}

function handleGetSmsCode() {
  if (countdown.value > 0 || isCaptchaLoading.value) {
    return
  }
  refreshGraphCaptcha()
}

async function confirmSendSms() {
  if (!captchaForm.captcha) {
    toast('请输入图形验证码')
    return
  }
  isSendingSms.value = true
  try {
    await getMobileCaptcha({
      mobile: form.mobile,
      captchaKey: captchaForm.captchaKey,
      captcha: captchaForm.captcha,
    })
    captchaDialogVisible.value = false
    startCountdown()
    toast('验证码已发送，请注意查收')
  } catch (error) {
    // 错误提示已在全局请求拦截器中统一处理
  } finally {
    isSendingSms.value = false
  }
}

function startCountdown() {
  countdown.value = 60
  if (countdownTimer) {
    clearInterval(countdownTimer)
  }
  countdownTimer = setInterval(() => {
    if (countdown.value <= 1) {
      clearInterval(countdownTimer)
      countdownTimer = null
      countdown.value = 0
      return
    }
    countdown.value -= 1
  }, 1000)
}

onBeforeUnmount(() => {
  if (countdownTimer) {
    clearInterval(countdownTimer)
    countdownTimer = null
  }
})

async function handleLogin() {
  if (!validateMobile()) {
    return
  }
  if (!SMS_CODE_PATTERN.test(form.smsCode)) {
    toast('请输入正确的短信验证码')
    return
  }
  if (!agreePolicy.value) {
    toast('请先勾选并同意服务协议和隐私协议')
    return
  }

  isLoginLoading.value = true
  try {
    const res = await mobileLogin({
      mobile: form.mobile,
      smsCode: form.smsCode,
    })
    const token =
      res?.data?.userinfo?.token ||
      res?.data?.token ||
      res?.data?.access_token ||
      res?.data?.accessToken ||
      res?.token ||
      ''
    if (token) {
      setToken(token)
      toast('登录成功')
      router.replace('/home')
      return
    }
    toast('登录成功，但未获取到登录凭证')
  } catch (error) {
    // 错误提示已在全局请求拦截器中统一处理
  } finally {
    isLoginLoading.value = false
  }
}
</script>

<style scoped lang="scss">
.login-page {
  min-height: 100vh;
  width: 100%;
  background: url('@/assets/img/login/login-bg.png') center center / cover no-repeat;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0 15vw;
}

.login-panel {
  position: relative;
  top: 26vw;
  width: 100%;
  max-width: 92.30769vw;
  z-index: 2;
}

.login-input {
  width: 100%;
  height: 13.33333vw;
  border: 0.25641vw solid #e8e8e8;
  border-radius: 0.51282vw;
  outline: none;
  padding: 0 3.07692vw;
  font-size: 4.10256vw;
  color: #333;
  background: #fff;
}

.login-input.full {
  width: 100%;
  margin-bottom: 6vw;
}

.login-input::placeholder {
  color: #999;
}

.code-row {
  display: flex;
  justify-content: space-between;
  gap: 3.58974vw;
}

.captcha-btn {
  width: 48vw;
  height: 13.33333vw;
  border: 0.25641vw solid #e8e8e8;
  border-radius: 0.51282vw;
  background: #fff;
  color: #666;
  font-size: 3.8vw;
  cursor: pointer;
}

.captcha-btn:disabled {
  color: #bbb;
  cursor: not-allowed;
}

.agreement {
  display: flex;
  align-items: center;
  gap: 3.07692vw;
  margin-top: 11.02564vw;
  color: #666;
  font-size: 3.6vw;
}

.agreement input {
  appearance: none;
  -webkit-appearance: none;
  width: 5.12821vw;
  height: 5.12821vw;
  border: 0.25641vw solid #d8d8d8;
  border-radius: 50%;
  background: #fff;
  cursor: pointer;
  position: relative;
  transition: all 0.2s ease;
}

.agreement input:checked {
  background: #ff5a1f;
  border-color: #ff5a1f;
}

.agreement input:checked::after {
  content: '';
  position: absolute;
  left: 1.53846vw;
  top: 0.76923vw;
  width: 1.28205vw;
  height: 2.30769vw;
  border: solid #fff;
  border-width: 0 0.51282vw 0.51282vw 0;
  transform: rotate(45deg);
}

.agreement em {
  color: #ff5b2d;
  font-style: normal;
}

.login-btn {
  position: relative;
  z-index: 2;
  margin-top: 12.30769vw;
  width: 100%;
  height: 12vw;
  border: none;
  border-radius: 7.05128vw;
  background: #ff5a1f;
  box-shadow: 0 2px 3px rgba(255, 215, 0, 0.5);
  color: #fff;
  font-size: 4.5vw;
  font-weight: 500;
  cursor: pointer;
}

.login-btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.login-bottom-image {
  position: relative;
  width: 100%;
  margin-top: -4vw;
  display: block;
  z-index: 1;
}

.captcha-dialog-content {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 2.5641vw;
}

.captcha-image {
  width: 100%;
  height: 16vw;
  border-radius: 1.28205vw;
  align-self: left;
  cursor: pointer;
}

.captcha-footer-actions {
  width: 100%;
  display: flex;
  justify-content: flex-start;
}

.captcha-input {
  width: 100%;
  height: 10vw;
}
</style>
